import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { BehaviorSubject, Observable } from 'rxjs';
import { concatMap, map, take, withLatestFrom } from 'rxjs/operators';

import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { ProductListingID, ProductListingView } from 'ish-core/models/product-listing/product-listing.model';
import { SelectOption } from 'ish-core/models/select-option/select-option.model';
import { ViewType } from 'ish-core/models/viewtype/viewtype.types';
import { whenFalsy, whenTruthy } from 'ish-core/utils/operators';
import { LoadingComponent } from 'ish-shared/components/common/loading/loading.component';
import { ProductListPagingComponent } from 'ish-shared/components/product/product-list-paging/product-list-paging.component';
import { ProductListToolbarComponent } from 'ish-shared/components/product/product-list-toolbar/product-list-toolbar.component';
import { ProductListComponent } from 'ish-shared/components/product/product-list/product-list.component';

@Component({
  selector: 'ish-product-listing',
  templateUrl: './product-listing.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // merged query parameters for product detail links are needed to apply previously selected filter options for variation masters too
  providers: [{ provide: 'PRODUCT_QUERY_PARAMS_HANDLING', useValue: 'merge' }],
  imports: [
    CommonModule,
    ProductListToolbarComponent,
    ProductListComponent,
    ProductListPagingComponent,
    InfiniteScrollDirective,
    LoadingComponent,
  ],
})
export class ProductListingComponent implements OnInit {
  private shoppingFacade = inject(ShoppingFacade);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  readonly categoryId = input<string>(undefined);
  // TODO: make this a proper signal with the next Angular version
  @Input() set productListingId(value: ProductListingID) {
    this.productListingId$.next(value);
  }
  readonly mode = input<'endless-scrolling' | 'paging'>('endless-scrolling');
  readonly fragmentOnRouting = input('product-list-top');
  productSortSelected = output<SelectOption>();

  productListingView$: Observable<ProductListingView>;
  viewType$: Observable<ViewType>;
  listingLoading$: Observable<boolean>;
  currentPage$: Observable<number>;
  sortBy$: Observable<string>;

  private productListingId$ = new BehaviorSubject<ProductListingID>(undefined);

  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.viewType$ = this.shoppingFacade.productListingViewType$;
    this.listingLoading$ = this.shoppingFacade.productListingLoading$;
    this.currentPage$ = this.activatedRoute.queryParamMap.pipe(map(params => +params.get('page') || 1));
    this.sortBy$ = this.activatedRoute.queryParamMap.pipe(map(params => params.get('sorting')));
    this.productListingView$ = this.shoppingFacade.productListingView$(this.productListingId$);

    // append view queryParam to URL if none is set
    this.activatedRoute.queryParamMap
      .pipe(
        map(params => params.has('view')),
        take(1),
        whenFalsy(),
        concatMap(() => this.viewType$.pipe(whenTruthy(), take(1))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(view => this.changeViewType(view));
  }

  /**
   * Emits the event for switching the view type of the product list.
   *
   * @param view The new view type.
   */
  private changeViewType(view: ViewType) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      replaceUrl: true,
      queryParamsHandling: 'merge',
      queryParams: { view },
    });
  }

  /**
   * Emits the event for loading more products.
   */
  loadMoreProducts(direction: 'up' | 'down') {
    this.productListingView$
      .pipe(
        take(1),
        map(view => (direction === 'down' ? view.nextPage() : view.previousPage())),
        whenTruthy(),
        withLatestFrom(this.productListingId$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([page, id]) => {
        this.shoppingFacade.loadMoreProducts(id, page);
      });
  }

  get isEndlessScrolling() {
    return this.mode() === 'endless-scrolling';
  }

  get isPaging() {
    return !this.isEndlessScrolling;
  }

  onProductSortSelected(option: SelectOption) {
    this.productSortSelected.emit(option);
  }
}
